/**
 * 70. Leave Requests — request flow; approvals; team calendar.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate } from "@/lib/money";

interface LeaveRow {
  id: string;
  leaveNumber: string;
  employee: string;
  leaveType: "Annual" | "Sick" | "Maternity" | "Paternity" | "Unpaid" | "Other";
  startDate: string;
  endDate: string;
  days: number;
  status: "Draft" | "Pending" | "Approved" | "Rejected" | "Cancelled";
}

export function LeaveRequestsPage() {
  const { data, isLoading } = useApiList<LeaveRow>(
    ["hr", "leave"],
    "/hr/leave",
  );

  return (
    <div>
      <PageHeader
        title="Leave Requests"
        subtitle="Request flow, approvals, and team calendar."
        actions={<Button variant="primary">New leave request</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Leave requests (${data.total})`}>
          <DataTable<LeaveRow>
            rows={data.items}
            columns={[
              {
                key: "leaveNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.leaveNumber}</span>
                ),
              },
              { key: "employee", label: "Employee" },
              { key: "leaveType", label: "Type" },
              {
                key: "startDate",
                label: "Start",
                render: (r) => formatDate(r.startDate),
              },
              {
                key: "endDate",
                label: "End",
                render: (r) => formatDate(r.endDate),
              },
              { key: "days", label: "Days", numeric: true },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Approved" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Rejected" || r.status === "Cancelled" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : r.status === "Pending" ? (
                    <Badge tone="warning">{r.status}</Badge>
                  ) : (
                    <Badge tone="default">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
